import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';

import { useHost } from '@xing-com/crate-xinglet';

import defaultImage from './assets/og-xing-logo.png';

type Props = {
  canonicalUrl?: string;
  description?: string;
  image?: string | null;
  keywords?: string;
  robots?: string;
  title?: string;
  ogTitle?: string;
};

export const Head: React.FC<Props> = ({
  canonicalUrl,
  description,
  image,
  keywords,
  robots,
  title,
  ogTitle,
}) => {
  const { formatMessage } = useIntl();
  const { isPreview } = useHost();
  const externalUrl = isPreview
    ? 'https://preview.xing.com'
    : 'https://www.xing.com';

  const defaultPageTitle = formatMessage({ id: 'SE_JOBS_PAGETITLE' });
  const defaultPageTitleSuffix = formatMessage({ id: 'SE_TITLEBAR_SUFFIX' });

  const defaultTitle = `${defaultPageTitle} | ${defaultPageTitleSuffix}`;
  const defaultOgTitle = title ?? `${defaultPageTitle}`;

  return (
    <Helmet>
      <title>{title ?? defaultTitle}</title>
      {description ? <meta name="description" content={description} /> : null}
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta property="og:title" content={ogTitle ?? defaultOgTitle} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="XING" />
      <meta
        property="og:image"
        content={image ?? `${externalUrl}${defaultImage}`}
      />
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
      {description ? (
        <meta property="og:description" content={description} />
      ) : null}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content="@XING_de" />
      {robots ? <meta name="robots" content={robots} /> : null}
      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
    </Helmet>
  );
};
